// Canonical public origin used when building externally-scannable links such as
// QR codes. A QR code embedded in an approved letter PDF must point to the public
// production URL so an external/guest user can scan it from a printed copy — it
// must never encode `localhost`, `127.0.0.1`, or a private LAN address (which is
// what `window.location.origin` resolves to when staff open the app locally).

const CANONICAL_PUBLIC_ORIGIN = (import.meta.env.VITE_LETTERS_PUBLIC_ORIGIN as string) || 'https://letter.surbhi.net';

const isNonPublicHost = (hostname: string): boolean => {
  const host = hostname.trim().toLowerCase();
  if (!host) return true;
  if (host === 'localhost' || host.endsWith('.localhost')) return true;
  if (host === '127.0.0.1' || host === '::1' || host === '0.0.0.0') return true;
  // Private/LAN ranges (RFC1918) that are unreachable from the public internet.
  if (/^10\./.test(host)) return true;
  if (/^192\.168\./.test(host)) return true;
  if (/^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(host)) return true;
  return false;
};

/**
 * Returns the origin that should be embedded in public-facing links (QR codes).
 * Prefers the current origin when it is a real public host so the app keeps
 * working on any deployed domain, but falls back to the canonical production URL
 * when running on localhost / a private network.
 */
export const getPublicOrigin = (): string => {
  if (typeof window === 'undefined' || !window.location) {
    return CANONICAL_PUBLIC_ORIGIN;
  }

  if (isNonPublicHost(window.location.hostname)) {
    return CANONICAL_PUBLIC_ORIGIN;
  }

  return window.location.origin;
};

/** Builds the public, scannable URL for a letter QR token. */
export const buildPublicQrUrl = (token: string): string =>
  `${getPublicOrigin()}/qr-letter/${encodeURIComponent(token)}`;
